import { DateTime } from 'luxon';
import { DateMode, FormFieldChoice, FormFieldType, type FormField } from './form';
import { DATE_FORMAT_7, formattedDate, getUTCJSDate } from '@/utils';
import { getInitialMatrixData } from '@/components';

export interface ISurgeonDashboard {
  perfusionSummaryData: FormField[];
    gases?: FormField[];
    timeSeriesTimepoints?: FormFieldChoice[];
    flows?: FormField[];
    labs?: FormField[];
    bile?: FormField[];
}

export class SurgeonDashboardData {
  perfusionSummaryData: FormField[];
  timeSeriesTimepoints: FormFieldChoice[];
  gases: FormField[];
  flows: FormField[];
  labs: FormField[];
  bile: FormField[];

  constructor(data: ISurgeonDashboard) {
    this.perfusionSummaryData = data.perfusionSummaryData;
    this.gases = data.gases ?? [];
    this.timeSeriesTimepoints = data.timeSeriesTimepoints ?? [];
    this.flows = data.flows ?? [];
    this.labs = data.labs ?? [];
    this.bile = data.bile ?? [];
  }

  static mapFromBackend(networkData: ISurgeonDashboard) {

    if (Object.keys(networkData).length == 0) {
      return undefined;
    }

    networkData.perfusionSummaryData?.forEach(formField => {
      const value = formField.value;

      switch (formField.type) {
        case FormFieldType.CHOICE:
        case FormFieldType.DROPDOWN:
          if (formField.options.maximumChoicesCount == 1) {
            if (value) {
              formField.value = formField.options.choices?.find(choice => choice.value == value)?.label;
            } else {
              formField.value = 'No data';
            }
            
          } else {
            const valueChoices = (value as string)?.split(',');
            const values: string[] = [];
            if (valueChoices) {
              valueChoices.forEach(choiceValue => {
                for (const choice of formField.options.choices!) {
                  if (choice.value == choiceValue) {
                    values.push(choice.label);
                    break;
                  }
                }
              });
            }
            formField.value = values.join(',');
          }
          break;

        case FormFieldType.DATE:
          if (value) {
            if (formField.options.mode === DateMode.DATE_TIME) {
              if (formField.options.absoluteDate) {
                formField.dateValue = getUTCJSDate(parseInt(value as string, 10));
              } else {
                formField.dateValue = DateTime.fromSeconds(parseInt(value as string)).toJSDate();
              }
            } else if (formField.options.mode === DateMode.DATE) {
              formField.dateValue = getUTCJSDate(parseInt(value as string, 10));
            } else if (formField.options.mode === DateMode.TIME) {
              const today = DateTime.now().startOf('day');
              formField.dateValue = today.plus({seconds: parseInt(value as string)}).toJSDate();
            }
            formField.value = formattedDate(formField.dateValue!, DATE_FORMAT_7);
          } else {
            formField.value = 'No data';
          }
          break;

        case FormFieldType.UNIT:
          if (value) {
            const unitValue = JSON.parse(value as string);
            formField.numberValue = parseFloat(unitValue.value);
            formField.value = formField.numberValue ? `${formField.numberValue?.toFixed(2)} ${unitValue.unit}` : 'No Data';
          } else {
            formField.value = 'No data';
          }
          break;

        case FormFieldType.NUMBER:
          formField.value = value ? `${Number(value).toFixed(2)}` : 'No data';
          break;

        case FormFieldType.FILE:
          if (value) {
            //@ts-expect-error hasTimePoints is for dashboard variables
            if (formField.hasTimePoints) {
              formField.images = JSON.parse(value as string);
            } else {
              formField.fileIds = JSON.parse(value as string);
            }
          }
          break;

        case FormFieldType.MATRIX:
          formField.matrixValues = value ? JSON.parse(value as string) : [];
          formField.matrixTableRowValues = getInitialMatrixData(formField);
          break;

        default:
          formField.value = value ? value : 'No data';
          break;
      }
    });

    const summaryData = networkData.perfusionSummaryData?.filter(field => field.value !== 'No data');

    return new SurgeonDashboardData({
      perfusionSummaryData: summaryData,
      gases: networkData.gases,
      timeSeriesTimepoints: networkData.timeSeriesTimepoints,
      flows: networkData.flows,
      labs: networkData.labs,
      bile: networkData.bile
    });
  }
}

// eslint-disable-next-line no-shadow
export enum SurgeonDashboardImageArrayType {
  LIVER_IMAGES,
  BIOPSY_IMAGES
}

export interface GraphGroup {
  label: string;
  expanded: boolean;
  data?: FormField[];
}

export interface GraphFilter {
  label: string;
  value: string;
  selected: boolean;
  key: string;
}

export interface GraphFilterGroup {
  id: number;
  label: string;
  filters: GraphFilter[];
}
